import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Upload, Download, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { SectionCard } from '../components/ui';
import { useToast, ToastContainer } from '../components/ui';
import { api } from '../api';

const CSV_TEMPLATE = `title,title_zh,description,category,tags,price_min,price_max,moq,lead_time_days,is_test
"Sample Product","示例产品","A great product","general","tag1;tag2",10,25,100,14,false`;

const PRODUCT_FIELDS = [
  { key: 'title', required: true },
  { key: 'title_zh', required: false },
  { key: 'description', required: false },
  { key: 'description_zh', required: false },
  { key: 'category', required: false },
  { key: 'tags', required: false },
  { key: 'price_min', required: false },
  { key: 'price_max', required: false },
  { key: 'moq', required: false },
  { key: 'lead_time_days', required: false },
  { key: 'is_test', required: false },
];

type Step = 'upload' | 'map' | 'preview' | 'done';

export default function AdminCsvImport() {
  const { t } = useTranslation(['adminProducts', 'products']);
  const { toasts, addToast, removeToast } = useToast();
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const [vendors, setVendors] = useState<Array<{ id: number; name: string }>>([]);
  const [vendorId, setVendorId] = useState<number | ''>('');
  const [step, setStep] = useState<Step>('upload');

  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});

  const [parsedResult, setParsedResult] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);

  useEffect(() => {
    api.getVendors().then((r: any) => setVendors((r.vendors || []).map((v: any) => ({ id: v.id, name: v.name })))).catch(() => {});
  }, []);

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (file: File) => {
    if (!vendorId) { addToast(t('csv.vendorRequired'), 'error'); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (!text?.trim()) { addToast('Empty file', 'error'); return; }
      const lines = text.trim().split('\n');
      if (lines.length < 2) { addToast('CSV must have header and data rows', 'error'); return; }

      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const rows = lines.slice(1).map(line => line.split(',').map(v => v.replace(/^"|"$/g, '').trim()));

      setRawHeaders(headers);
      setRawRows(rows);

      const autoMap: Record<string, string> = {};
      headers.forEach(h => {
        const lower = h.toLowerCase().replace(/[\s_-]+/g, '');
        const match = PRODUCT_FIELDS.find(f => f.key.toLowerCase().replace(/_/g, '') === lower);
        if (match) autoMap[h] = match.key;
      });
      setColumnMap(autoMap);

      setStep('map');
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleMapConfirm = async () => {
    if (!vendorId) return;

    const mappedLines = [Object.values(columnMap).join(',')];
    rawRows.forEach(row => {
      const mapped: string[] = [];
      for (const header of rawHeaders) {
        const target = columnMap[header];
        if (target) {
          const idx = rawHeaders.indexOf(header);
          mapped.push(`"${(row[idx] || '').replace(/"/g, '""')}"`);
        }
      }
      if (mapped.length === Object.keys(columnMap).filter(k => columnMap[k]).length) {
        mappedLines.push(mapped.join(','));
      }
    });

    try {
      const result = await api.adminImportCsvParse(vendorId as number, mappedLines.join('\n'));
      setParsedResult(result.data);
      setStep('preview');
    } catch {
      addToast('Failed to parse CSV', 'error');
    }
  };

  const handleImportConfirm = async () => {
    if (!vendorId || !parsedResult?.rows) return;
    setImporting(true);
    try {
      const validRows = parsedResult.rows.filter((r: any) => r.errors.length === 0);
      const result = await api.adminImportCsvConfirm(vendorId as number, validRows);
      setImportedCount(result.data.created);
      setStep('done');
    } catch {
      addToast(t('csv.failed'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const inputStyle = { border: '1px solid rgba(255,255,255,0.10)', borderRadius: '2px', backgroundColor: 'rgba(255,255,255,0.03)' };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <button onClick={() => navigate('/admin/products')} className="flex items-center gap-1 text-xs text-white/40 hover:text-white/70 mb-3 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> {t('detail.backToProducts')}
        </button>
        <h1 className="text-xl font-display font-bold text-white">{t('csv.title')}</h1>
        <p className="text-sm text-white/40 mt-1">{t('csv.subtitle')}</p>
      </div>

      {/* Vendor selector */}
      <SectionCard title={t('csv.selectVendor')}>
        <div className="p-5 flex items-center gap-4">
          <select
            value={vendorId}
            onChange={e => setVendorId(e.target.value ? parseInt(e.target.value) : '')}
            className="flex-1 py-2 px-3 text-sm text-white/80 bg-transparent outline-none max-w-sm"
            style={inputStyle}
          >
            <option value="">{t('form.selectVendor')}</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button onClick={downloadTemplate} className="btn-ghost text-xs flex items-center gap-1">
            <Download className="w-3.5 h-3.5" /> {t('csv.downloadTemplate')}
          </button>
        </div>
      </SectionCard>

      {/* Upload */}
      {step === 'upload' && (
        <div
          className="card p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors hover:bg-white/[0.03]"
          style={{ border: '2px dashed rgba(255,255,255,0.10)', minHeight: '200px' }}
          onClick={() => fileInput.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Upload className="w-8 h-8 text-white/20" />
          <p className="text-sm text-white/40">{t('csv.dragDrop')}</p>
          <input ref={fileInput} type="file" accept=".csv" className="hidden" onChange={e => { if (e.target.files?.[0]) handleFileUpload(e.target.files[0]); }} />
        </div>
      )}

      {/* Column Mapping */}
      {step === 'map' && (
        <SectionCard title={t('csv.columnMapping')}>
          <div className="p-5 space-y-3">
            <p className="text-xs text-white/40 mb-3">
              {rawRows.length} data rows detected · {rawHeaders.length} columns
            </p>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-3 py-2 text-white/30">{t('csv.sourceColumn')}</th>
                  <th className="text-left px-3 py-2 text-white/30">{t('csv.targetField')}</th>
                  <th className="text-left px-3 py-2 text-white/30">Sample</th>
                </tr>
              </thead>
              <tbody>
                {rawHeaders.map((header, i) => (
                  <tr key={i} className="border-b border-white/[0.03]">
                    <td className="px-3 py-2 text-white/60">{header}</td>
                    <td className="px-3 py-2">
                      <select
                        value={columnMap[header] || ''}
                        onChange={e => setColumnMap({ ...columnMap, [header]: e.target.value })}
                        className="text-xs px-2 py-1 text-white/60 bg-transparent outline-none"
                        style={inputStyle}
                      >
                        <option value="">{t('csv.skip')}</option>
                        {PRODUCT_FIELDS.map(f => <option key={f.key} value={f.key}>{f.key}{f.required ? ' *' : ''}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-white/30">{rawRows[0]?.[i] || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-2 justify-end pt-3">
              <button onClick={() => { setStep('upload'); setRawHeaders([]); setRawRows([]); }} className="btn-ghost text-xs">{t('csv.cancel')}</button>
              <button onClick={handleMapConfirm} className="btn-primary text-xs" disabled={!Object.values(columnMap).includes('title')}>
                {t('csv.preview')}
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Preview */}
      {step === 'preview' && parsedResult && (
        <SectionCard title={t('csv.preview')}>
          <div className="p-5 space-y-4">
            <div className="flex gap-4 text-sm">
              <span className="text-white/50">{t('csv.rowsFound', { count: parsedResult.total })}</span>
              <span className="text-[#4ade80]">{t('csv.valid', { count: parsedResult.valid })}</span>
              {parsedResult.errors > 0 && <span className="text-[#f87171]">{t('csv.errors', { count: parsedResult.errors })}</span>}
            </div>

            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left px-3 py-2 text-white/30">{t('csv.row')}</th>
                    <th className="text-left px-3 py-2 text-white/30">{t('csv.titleCol')}</th>
                    <th className="text-left px-3 py-2 text-white/30">{t('csv.categoryCol')}</th>
                    <th className="text-left px-3 py-2 text-white/30">{t('csv.statusCol')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedResult.rows.slice(0, 20).map((row: any) => (
                    <tr key={row.row} className="border-b border-white/[0.03]">
                      <td className="px-3 py-2 text-white/40">{row.row}</td>
                      <td className="px-3 py-2 text-white/60">{row.data.title || '—'}</td>
                      <td className="px-3 py-2 text-white/40">{row.data.category || '—'}</td>
                      <td className="px-3 py-2">
                        {row.errors.length === 0 ? (
                          <CheckCircle className="w-3.5 h-3.5 text-[#4ade80]" />
                        ) : (
                          <div className="flex items-center gap-1 text-[#f87171]">
                            <AlertCircle className="w-3.5 h-3.5" />
                            <span>{row.errors.join(', ')}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setStep('map')} className="btn-ghost text-xs">{t('csv.cancel')}</button>
              <button onClick={handleImportConfirm} disabled={importing || parsedResult.valid === 0} className="btn-primary text-xs flex items-center gap-1.5">
                {importing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('csv.importing')}</> : t('csv.confirm')}
              </button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Done */}
      {step === 'done' && (
        <div className="card p-8 flex flex-col items-center gap-4">
          <CheckCircle className="w-12 h-12 text-[#4ade80]" />
          <p className="text-lg font-display text-white">{t('csv.success', { count: importedCount })}</p>
          <button onClick={() => navigate('/admin/products')} className="btn-primary text-sm">{t('detail.backToProducts')}</button>
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}
